import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Button, CircularProgress, Typography, Box } from '@mui/material';

export interface AeroSyncWidgetModalProps {
    open: boolean;
    isLinking: boolean;
    onClose: () => void;
    loadingMessage?: string;
}

export const AeroSyncWidgetModal: React.FC<AeroSyncWidgetModalProps> = ({
    open,
    isLinking,
    onClose,
    loadingMessage = 'Linking your bank account...',
}) => {
    const portalRoot = useRef<HTMLDivElement | null>(null);
    const sdkContainer = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        // Portal wrapper — outside React root
        const portal = document.createElement('div');
        portal.id = 'aerosync-portal-root';
        document.body.appendChild(portal);
        portalRoot.current = portal;

        // SDK container — a raw div React never touches
        // The SDK will inject/remove iframes here directly
        const container = document.createElement('div');
        container.id = 'aerosync-widget-container';
        container.style.width = '100%';
        container.style.height = '100%';
        sdkContainer.current = container;

        return () => {
            if (portalRoot.current && document.body.contains(portalRoot.current)) {
                document.body.removeChild(portalRoot.current);
            }
            portalRoot.current = null;
            sdkContainer.current = null;
        };
    }, []);

    // Attach/detach the raw SDK container div when modal opens/closes
    useEffect(() => {
        if (!sdkContainer.current) return;

        const modalInner = document.getElementById('aerosync-modal-inner');
        if (!modalInner) return;

        if (open) {
            if (!modalInner.contains(sdkContainer.current)) {
                modalInner.appendChild(sdkContainer.current);
            }
        } else {
            if (modalInner.contains(sdkContainer.current)) {
                modalInner.removeChild(sdkContainer.current);
            }
        }
    }, [open]);

    if (!portalRoot.current) return null;
    if (!open) return null;

    return ReactDOM.createPortal(
        <Box
            position="fixed"
            top={0}
            left={0}
            width="100vw"
            height="100vh"
            bgcolor="rgba(0,0,0,0.8)"
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{ zIndex: 9999 }}
        >
            <Box
                sx={{
                    width: '375px',
                    height: '688px',
                    position: 'relative',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    bgcolor: '#141414',
                }}
            >
                {/* <Button
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        top: 15,
                        right: 20,
                        zIndex: 10000,
                        color: '#fff',
                        minWidth: 'auto',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                    }}
                >
                    ✕
                </Button> */}

                {isLinking && (
                    <Box
                        position="absolute"
                        top={0}
                        left={0}
                        width="100%"
                        height="100%"
                        bgcolor="rgba(0,0,0,0.75)"
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                        sx={{ zIndex: 10001, borderRadius: '12px' }}
                    >
                        <CircularProgress color="primary" size={48} />
                        <Typography sx={{ color: '#fff', mt: 2, fontSize: 16 }}>
                            {loadingMessage}
                        </Typography>
                    </Box>
                )}

                {/*
                 * This div is the mount point for sdkContainer.
                 * React renders this div but never puts children inside it —
                 * the raw DOM div (sdkContainer ref) is appended imperatively.
                 */}
                <div
                    id="aerosync-modal-inner"
                    style={{ width: '100%', height: '100%' }}
                />
            </Box>
        </Box>,
        portalRoot.current
    );
};

export default AeroSyncWidgetModal;